// Which effect a recorded event gets, how big, and when. Pure functions (tested in logic.test.ts); the effects are
// illustrative: they mark where and when the record puts an event, scaled by its recorded deaths or, failing that,
// by how widely it is documented (Wikipedia languages). They never stand for weapons or casualties.

import type { GedYear, NamedEvents } from "../lib/data";

export const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));

// --- weapons by period: what a battle looks and sounds like ------------------------------------------

export const ERA_BLADE = 0; // before guns mattered on battlefields: dust, fire, clashing steel
export const ERA_POWDER = 1; // black powder: muzzle flashes, white smoke, cannon and musket volleys
export const ERA_RIFLE = 2; // rifled artillery, still much powder smoke
export const ERA_SHELL = 3; // high explosive: fireballs, black smoke, machine guns, aircraft
export const ERA_MODERN = 4;

export function eraOf(t: number): number {
  return t < 1420 ? ERA_BLADE : t < 1860 ? ERA_POWDER : t < 1912 ? ERA_RIFLE : t < 1960 ? ERA_SHELL : ERA_MODERN;
}

const ERA_START = [-1e9, 1420, 1860, 1912, 1960, 1e9];

/** t, kept inside period `era`: an event whose picture follows its start date (an imprecise date drawn at a later
 * moment, a long war) is heard in the same period it is seen in. */
export function eraTime(t: number, era: number): number {
  return Math.min(Math.max(t, ERA_START[era]), ERA_START[era + 1] - 0.01);
}

// --- kinds of effect ----------------------------------------------------------------------------------

export const FX_NONE = -1;
export const FX_BATTLE = 0;
export const FX_SIEGE = 1; // a bombardment, then the place burns while the siege lasts
export const FX_NAVAL = 2; // splashes and burning ships
export const FX_MEMORIAL = 3; // massacres, deportations, violence against civilians: a quiet pulse, falling ash, a bell
export const FX_BOMBING = 4; // air raids: a line of explosions
export const FX_UPRISING = 5; // street fighting: small fires, gunfire
export const FX_NUKE = 6;

const NUCLEAR = new Set(["Q703203", "Q4382340"]); // the atomic bombings of Hiroshima and of Nagasaki
const NUCLEAR_BOTH = "Q488"; // the item for both bombings: drawn through the two above
const AIR_RAID = /\b(bomb(ing|ings|ardment)?|blitz|air raids?|air strikes?|airstrikes?)\b/i;
// Atrocities are never shown as blasts, whatever the record files them under (many roundups, "actions" and
// executions are "attacks" or "operations"; a camp's liberation is an "operation").
const ATROCITY = new RegExp([
  "concentration camp", "extermination camp", "death camp", "holocaust", "genocide", "massacres?", "pogroms?",
  "deportations?", "death march", "round-?ups?", "rafles?", "razzias?", "\\baktion\\b", "\\bghettos?\\b", "executions?",
  "\\bexecuted\\b", "shootings?", "mass killings?", "murders?", "lynching", "purges?", "ethnic cleansing", "expulsions?",
  "persecution", "abductions?", "kidnapping", "night of the long knives", "kristallnacht", "death squads?",
  "forced labou?r", "internment",
].join("|"), "i");
// strikes on people and places named as civilian: a hospital, a school, a refugee camp, a wedding, a civilian convoy…
const TARGET = "hospitals?|clinics?|schools?|kindergartens?|universit(?:y|ies)|refugees?|mosques?|church(?:es)?|cathedrals?|" +
  "synagogues?|temples?|weddings?|civilians?|famil(?:y|ies)|markets?|marketplaces?|aid convoys?|humanitarian";
const STRIKE = "air ?strikes?|bombings?|bombardments?|shelling|attacks?|explosions?|killings?|storming|raids?|strikes?|ambush(?:es)?";
const ON_CIVILIANS = new RegExp(`\\b(?:${TARGET})\\b[^,]{0,25}\\b(?:${STRIKE})\\b|\\b(?:${STRIKE})\\b[^,]{0,30}\\b(?:${TARGET})\\b`, "i");
// atrocities whose names say none of that
const ATROCITY_IDS = new Set([
  "Q153325", // Operation Harvest Festival (Aktion Erntefest): Majdanek, Poniatowa and Trawniki, November 1943
  "Q422352", // Dünamünde Action, Riga, 1942
  "Q56327583", // Action in Pinsk, 1942–43
  "Q125256491", // the army's terror campaign in the Cottica area, Suriname, 1986
  "Q3521478", // the Killing Time: the persecution of the Covenanters, 1680s
  "Q123865146", // three Israeli hostages shot by mistake in Gaza, December 2023
]);

/** True when the record's event is an atrocity: drawn as a memorial, never as a blast. */
export function isAtrocity(kind: number, name: string, id: string): boolean {
  return kind === 3 || kind === 8 || kind === 10 || ATROCITY_IDS.has(id) || ATROCITY.test(name) || ON_CIVILIANS.test(name);
}

/** Event kinds (colors.ts): 0 battle, 1 siege, 2 naval, 3 massacre, 4 attack, 5 operation, 6–8 UCDP, 9 uprising,
 * 10 deportation. */
export function effectOf(kind: number, name: string, id: string, t: number): number {
  if (id === NUCLEAR_BOTH) return FX_NONE;
  if (NUCLEAR.has(id)) return FX_NUKE;
  if (isAtrocity(kind, name, id)) return FX_MEMORIAL;
  if (kind === 2) return FX_NAVAL;
  if (kind === 1) return FX_SIEGE;
  if (kind === 9) return FX_UPRISING;
  if (kind === 4 && t >= 1914 && AIR_RAID.test(name)) return FX_BOMBING;
  return FX_BATTLE;
}

/** 0–1: recorded deaths on a log scale (≈316,000 → 1) or, failing that, documentation (≈100 languages → 1). */
export function namedMagnitude(deaths: number, sitelinks: number): number {
  const byDeaths = deaths > 0 ? Math.log10(deaths) / 5.5 : 0;
  const byFame = Math.log2(1 + sitelinks) / 6.6;
  return clamp(Math.max(byDeaths, byFame), 0.08, 1);
}

/** 0–0.85 for a UCDP event by its best estimate of deaths (1 → 0.08, 10 → 0.29, 100 → 0.56, 1,000 → 0.83). */
export function gedMagnitude(best: number): number {
  return clamp(Math.log10(1 + Math.max(0, best)) / 3.6, 0.04, 0.85);
}

// --- events indexed by the time their effect fires -------------------------------------------------------

export interface FxEvents {
  src: "named" | "ged";
  n: number;
  t: Float64Array; // when the effect fires, ascending
  t1: Float64Array; // the event's end
  lon: Float32Array;
  lat: Float32Array;
  mag: Float32Array;
  fx: Int8Array;
  era: Uint8Array;
  c: Int32Array; // conflict index, -1 when none
  kind: Uint8Array; // event kind (colors.ts)
  burn: Uint8Array; // 1: a precisely dated event of several days (a siege, a campaign): burns while it lasts
  id: string[] | null; // Wikidata or curated id (named events)
  maxBurn: number; // the longest burning event (years), to bound the search for what burns at a moment
}

const DAY = 1 / 365.25;
export const MIN_BURN = 3 * DAY; // shorter events flare up and settle; longer ones keep burning while they last
const MAX_BURN = 3; // years; longer "battles" are campaigns or dating errors, not one fire

/** Deterministic 0–1 from a string (FNV-1a). */
export function hash01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 4294967296;
}

/** Index a named-events chunk. An event dated only to its month or year fires at a point inside that range picked
 * from its id, so a year's imprecisely dated battles do not all explode on 1 January. */
export function indexNamed(ch: NamedEvents): FxEvents {
  const n = ch.n;
  const tf = new Float64Array(n);
  for (let j = 0; j < n; j++) {
    const imprecise = ch.pr[j] < 11 && ch.t1[j] > ch.t0[j];
    tf[j] = imprecise ? ch.t0[j] + hash01(ch.id[j]) * (ch.t1[j] - ch.t0[j]) * 0.92 : ch.t0[j];
  }
  const order = Array.from({ length: n }, (_, j) => j).sort((a, b) => tf[a] - tf[b]);
  const out = alloc("named", n, true);
  order.forEach((j, k) => {
    out.t[k] = tf[j];
    out.t1[k] = ch.t1[j];
    out.lon[k] = ch.lon[j];
    out.lat[k] = ch.lat[j];
    out.mag[k] = namedMagnitude(ch.d[j], ch.s[j]);
    out.fx[k] = effectOf(ch.k[j], ch.name[j], ch.id[j], ch.t0[j]);
    out.era[k] = eraOf(ch.t0[j]);
    out.c[k] = ch.c[j];
    out.kind[k] = ch.k[j];
    const dur = ch.t1[j] - ch.t0[j];
    out.burn[k] = ch.pr[j] >= 11 && dur >= MIN_BURN && dur <= MAX_BURN && out.fx[k] !== FX_MEMORIAL ? 1 : 0;
    if (out.burn[k]) out.maxBurn = Math.max(out.maxBurn, dur);
    out.id![k] = ch.id[j];
  });
  return out;
}

/** Index a year of UCDP events (already in date order). */
export function indexGed(y: GedYear): FxEvents {
  const out = alloc("ged", y.n, false);
  for (let j = 0; j < y.n; j++) {
    out.t[j] = y.t0[j];
    out.t1[j] = y.t1[j];
    out.lon[j] = y.lon[j];
    out.lat[j] = y.lat[j];
    out.mag[j] = gedMagnitude(y.b[j]);
    // UCDP's type of violence, as the export writes it (export.py: kind − 5): 1 state-based, 2 non-state, 3 one-sided
    // violence against civilians, which is never a battle
    out.fx[j] = y.ty[j] === 3 ? FX_MEMORIAL : FX_BATTLE;
    out.era[j] = ERA_MODERN;
    out.c[j] = y.c[j];
    out.kind[j] = 5 + y.ty[j];
  }
  return out;
}

function alloc(src: FxEvents["src"], n: number, ids: boolean): FxEvents {
  return {
    src, n, t: new Float64Array(n), t1: new Float64Array(n), lon: new Float32Array(n), lat: new Float32Array(n),
    mag: new Float32Array(n), fx: new Int8Array(n), era: new Uint8Array(n), c: new Int32Array(n), kind: new Uint8Array(n),
    burn: new Uint8Array(n), id: ids ? new Array<string>(n) : null, maxBurn: 0,
  };
}

/** First index whose value is greater than x (arr ascending). */
export function upperBound(arr: Float64Array, x: number, n = arr.length): number {
  let lo = 0, hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] <= x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Events whose effect fires in (a, b]: the index range [lo, hi). */
export function crossed(ev: FxEvents, a: number, b: number): [number, number] {
  if (!(b > a)) return [0, 0];
  return [upperBound(ev.t, a, ev.n), upperBound(ev.t, b, ev.n)];
}

/** Long events burning at time t (they started, they have not ended). */
export function burningAt(ev: FxEvents, t: number): number[] {
  const out: number[] = [];
  if (!ev.maxBurn) return out;
  for (let k = upperBound(ev.t, t, ev.n) - 1; k >= 0 && ev.t[k] >= t - ev.maxBurn; k--) {
    if (ev.burn[k] && ev.t1[k] > t) out.push(k);
  }
  return out;
}

/** How many events of a war's family (focus mask) fired in (a, b]. */
export function countFocus(sources: FxEvents[], focus: Uint8Array, a: number, b: number): number {
  let n = 0;
  for (const ev of sources) {
    if (ev.src !== "named") continue;
    const [lo, hi] = crossed(ev, a, b);
    for (let k = lo; k < hi; k++) if (ev.c[k] >= 0 && focus[ev.c[k]] && ev.fx[k] !== FX_NONE) n++;
  }
  return n;
}

// --- pacing: how many explosions the screen and the ears can take ---------------------------------------------

/** A token bucket: up to `cap` at once, refilled at `rate` per second. */
export class Bucket {
  private tokens: number;
  constructor(readonly cap: number, readonly rate: number) {
    this.tokens = cap;
  }
  refill(dt: number) {
    this.tokens = Math.min(this.cap, this.tokens + dt * this.rate);
  }
  take(n = 1): boolean {
    if (this.tokens < n) return false;
    this.tokens -= n;
    return true;
  }
}

export const SKIP = 0, POP = 1, FULL = 2;

/** Decide, for candidates sorted by magnitude (largest first), which get a full effect, which only a flash ("pop")
 * and which are left out. Tiny events never get a full effect. */
export function triage(mags: ArrayLike<number>, full: Bucket, pop: Bucket, maxFull = 3, minFull = 0.16): Uint8Array {
  const out = new Uint8Array(mags.length);
  let nFull = 0;
  for (let k = 0; k < mags.length; k++) {
    if (mags[k] >= minFull && nFull < maxFull && full.take()) {
      out[k] = FULL;
      nFull++;
    } else if (pop.take()) out[k] = POP;
  }
  return out;
}

// --- screen and sound geometry -----------------------------------------------------------------------------

/** Where a sound comes from: 0 at the centre of the view, ~0.45 at its edge, 1 far off screen; pan from x. */
export function hearing(x: number, y: number, w: number, h: number): { dist: number; pan: number } {
  const dx = (x - w / 2) / Math.max(1, w / 2), dy = (y - h / 2) / Math.max(1, h / 2);
  return { dist: clamp(Math.hypot(dx, dy * 0.8) / 2.2, 0, 1), pan: clamp(dx * 0.7, -0.85, 0.85) };
}

/** Camera shake from "trauma" (0–1; felt as its square): x, y in pixels and a small rotation in degrees. */
export function shakeAt(trauma: number, time: number, maxPx = 11, maxDeg = 0.5): [number, number, number] {
  const k = trauma * trauma;
  const n = (s: number) => Math.sin(time * 37.1 + s) * 0.6 + Math.sin(time * 61.7 + s * 2.3) * 0.4;
  return [maxPx * k * n(1.3), maxPx * k * n(7.9), maxDeg * k * n(4.2)];
}

/** The replay camera's tilt for a zoom: low over continents, steeper over a battlefield. */
export function cinematicPitch(zoom: number): number {
  return clamp(20 + (zoom - 2.5) * 10, 18, 54);
}

/** Pixels per effect unit at a zoom level (effects grow, gently, as one zooms in). */
export function unitPx(zoom: number): number {
  return clamp(0.95 * Math.pow(2, (zoom - 3) * 0.55), 0.5, 4.2);
}

/** The film look of a period (cinematic level): applied to the base map only, never to the data. */
export function gradeOf(t: number): "ancient" | "early" | "industrial" | "newsreel" | "cold" | "modern" {
  return t < 1500 ? "ancient" : t < 1800 ? "early" : t < 1914 ? "industrial" : t < 1950 ? "newsreel" : t < 1992 ? "cold" : "modern";
}
